import request from "graphql-request";
import { useRecordSignedIn } from "hooks/analytics/useRecordSignedIn";
import { useRecordSignedOut } from "hooks/analytics/useRecordSignedOut";
import useFirebaseSession from "hooks/useFirebaseSession";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { baseUrl } from "services/shared/Environment";
import useSWR from "swr";

function getUserQuery(firebaseId: string) {
  return `
      query {
        user(firebaseId: "${firebaseId}") {
          id
          firebaseId
          exists
          firstName
          lastName
          email
          stripeCustomerId
          stripePlanId
          subscriptionHasExpired
        }
      }
  `;
}

export interface UserContextValue {
  firebaseId: string;
  email: string;
  firstName: string;
  lastName: string;
  stripeCustomerId: string;
  stripePlanId: string;
  userIsSubscriber: boolean;
  loggedIn: boolean;
  initialized: boolean;
  updates: number;
  previousValue?: Pick<
    UserContextValue,
    | "email"
    | "firstName"
    | "lastName"
    | "stripeCustomerId"
    | "stripePlanId"
    | "userIsSubscriber"
    | "loggedIn"
    | "updates"
  >;
}

const defaultContextValue: UserContextValue = {
  firebaseId: "",
  email: "",
  firstName: "",
  lastName: "",
  stripeCustomerId: "",
  stripePlanId: "",
  userIsSubscriber: false,
  loggedIn: false,
  initialized: false,
  updates: 0,
  previousValue: undefined,
};

export const UserContext = createContext(defaultContextValue);

export const UserContextProvider: React.FC = ({ children }) => {
  const [value, setValue] = useState(defaultContextValue);
  useUpdateContextValue(value, (updatedValue) =>
    setValue((previousValue) => ({
      ...updatedValue,
      updates: previousValue.updates + 1,
      previousValue,
    }))
  );

  const recordSignedIn = useRecordSignedIn();
  const recordSignedOut = useRecordSignedOut();

  useEffect(() => {
    if (value.loggedIn && value.updates >= 2) {
      recordSignedIn(value);
    }
    if (!value.loggedIn && value.updates >= 2) {
      recordSignedOut(value.previousValue);
    }
  }, [value]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export function useUserContext() {
  const user = useContext(UserContext);
  return user;
}

function useUpdateContextValue(
  currentValue: UserContextValue,
  onValueUpdated: (
    value: Pick<
      UserContextValue,
      | "firebaseId"
      | "firstName"
      | "lastName"
      | "email"
      | "stripeCustomerId"
      | "stripePlanId"
      | "userIsSubscriber"
      | "loggedIn"
      | "initialized"
    >
  ) => void
) {
  const { user: firebaseUser, sessionInitialized } = useFirebaseSession();

  const shouldPoll = useMemo(() => {
    const accountInfoMissing = currentValue?.email.length === 0;
    return firebaseUser?.uid && accountInfoMissing;
  }, [firebaseUser?.uid, currentValue?.email]);

  const swrKey = useMemo(() => {
    const id = firebaseUser?.uid ?? "_"; // '_' is used to avoid key being falsey
    return sessionInitialized ? id : null;
  }, [firebaseUser, sessionInitialized]);

  const { data, error } = useSWR(swrKey, getContextValue, {
    refreshInterval: shouldPoll ? 100 : 0,
  });

  useEffect(() => {
    const loadedUser = Boolean(data && !error);
    const errorLoadingUser = Boolean(error);

    if (sessionInitialized && loadedUser) {
      onValueUpdated(data);
    } else if (sessionInitialized && errorLoadingUser) {
      onValueUpdated({
        ...defaultContextValue,
        initialized: true,
      });
    }
  }, [data, error, sessionInitialized]);
}

async function getContextValue(firebaseUserId?: string) {
  try {
    const result = await request(
      `${baseUrl()}/api/graphql`,
      getUserQuery(firebaseUserId)
    );
    const user = result?.user;
    return {
      firebaseId: user?.firebaseId ?? '',
      email: user?.email ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      stripeCustomerId: user?.stripeCustomerId ?? "",
      stripePlanId: user?.stripePlanId ?? "",
      userIsSubscriber: !(user?.subscriptionHasExpired ?? true),
      loggedIn: user?.exists ?? false,
      initialized: true,
    };
  } catch (e) {
    return {
      ...defaultContextValue,
      initialized: true,
      loggedIn: false,
    };
  }
}
